<script setup>
import { ref, computed } from 'vue';
import TextInput from '@/components/forms/inputs/text-input.vue';
import Checkbox from '@/components/checkbox.vue';
import ButtonLoading from '@/components/button-loading.vue';
import { useLayoutComponents } from '@/composables/layout.js';

const { Navbar } = useLayoutComponents();

const loading = ref(false);

const formData = ref({
  username: '',
  name: '',
  productionName: '',
  address: '',
  termsAccepted: false
});

const isFormValid = computed(() => {
  return formData.value.username &&
    formData.value.name &&
    formData.value.termsAccepted;
});

const submitForm = () => {
  if (isFormValid.value) {
    loading.value = true;
  }
};
</script>
<template>
  <div class="hero-container">
    <Navbar />
  </div>
  <div class="flex min-h-[calc(100vh-4.5rem)] w-full ">
    <div class="flex-1 bg-gray-900 text-white px-20 flex flex-col justify-center items-center">
      <h1
        class="text-2xl 2xl:text-3xl font-bold mb-0.5 bg-gradient-to-r from-[#FFFFFF] to-[#999999] bg-clip-text text-transparent w-fit">
        You have been invited to join the RenderyDAO!</h1>
      <p
        class="text-base 2xl:text-lg font-semibold bg-gradient-to-r from-[#FFFFFF] to-[#999999] bg-clip-text text-transparent w-fit mb-10">
        Please enter the information below to get started.</p>
      <div class="grid grid-cols-2 gap-6 mb-8 ">
        <div class="flex flex-col gap-2">
          <label for="username" class="text-sm 2xl:text-base font-medium flex items-center">
            Username
          </label>
          <text-input id="username" v-model="formData.username" placeholder="Select your username." :required="true" />
        </div>
        <div class="flex flex-col gap-2">
          <label for="name" class="text-sm 2xl:text-base font-medium flex items-center">
            Name
          </label>
          <text-input id="name" v-model="formData.name" placeholder="Your full name, or the name of your organization."
            :required="true" />
        </div>
        <div class="flex flex-col gap-2">
          <label for="address" class="text-sm 2xl:text-base font-medium flex items-center">Address</label>
          <text-input id="address" v-model="formData.address"
            placeholder="E.g. &quot;0xffC56F18dbe02Dc48...c110AA98501B&quot;" />
          <div class="text-xs 2xl:text-sm text-white/70 mt-1">
            You must provide an address you control to sell products on our Datalab.
            <a href="#" class="text-blue-400 underline">Learn more</a>.
          </div>
        </div>
        <div class="flex flex-col gap-2">
          <label for="production-name" class="text-sm 2xl:text-base font-medium flex items-center">Production
            Name</label>
          <text-input id="production-name" v-model="formData.productionName"
            placeholder="E.g. &quot;0xffC56F18dbe02Dc48...c110AA98501B&quot;" />
        </div>
      </div>
      <div class="mt-6 mx-auto w-full flex flex-col gap-4 items-center">
        <label for="terms-accepted" class="flex items-center cursor-pointer select-none gap-2 text-sm">
          <Checkbox v-model:checked="formData.termsAccepted" />
          <span class="ml-0.5 2xl:text-lg">I have read and agreed with the Terms of Use</span>
        </label>
        <button @click="submitForm" :disabled="!isFormValid || loading"
          class="flex items-center justify-between bg-gradient-to-br from-green-500 to-green-700 hover:bg-gradient-to-br hover:from-green-600 hover:to-green-800 text-white font-semibold py-2.5 2xl:py-3.5 px-6 w-full lg:w-1/2  max-w-xs 2xl:max-w-md mx-auto 2xl:scale-105 rounded-xl text-sm 2xl:text-base">
          <ButtonLoading v-if="loading" size="sm" text="Registering..." />
          <template v-else>Onboard with Stripe to Continue</template>
          <svg class="w-6 h-6 text-gray-800 dark:text-white" aria-hidden="true" xmlns="http://www.w3.org/2000/svg"
            width="24" height="24" fill="none" viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="M19 12H5m14 0-4 4m4-4-4-4" />
          </svg>
        </button>
      </div>
    </div>
    <div
      class="w-1/3 bg-gradient-to-b from-gray-200 to-neutral-600 p-8 min-h-[calc(100vh-4.5rem)] flex flex-col justify-center items-center text-center gap-3 2xl:gap-6">
      <div class="logo-container">
        <div class="logo-text-container">
          <h2 class="logo-text-title">Rendery</h2>
          <h2 class="logo-text-subtitle">DAO</h2>
        </div>
      </div>
      <div class="text-lg 2xl:text-2xl font-semibold text-neutral-800">@rendery.dao</div>
      <div class="text-gray-800 text-sm 2xl:text-base leading-relaxed max-w-xs 2xl:max-w-lg">
        RenderyDAO is a collective of video creators. Our mission is to allows creators to own a share of the AI
        revolution
        by making it easier to monetize IP.
      </div>

      <div class="w-full flex flex-col gap-3 2xl:gap-4 max-w-sm 2xl:max-w-xl">
        <div class="bg-neutral-300 rounded-lg px-4 py-3 2xl:px-6 2xl:py-4 w-full">
          <div class="flex items-center font-medium text-sm 2xl:text-base text-gray-800">
            <span class="mr-2 text-purple-600 font-bold"><svg class="w-6 h-6 text-gray-800" aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="m9 5 7 7-7 7" />
              </svg>
            </span> How much money can I make?
          </div>
        </div>
        <div class="bg-neutral-300 rounded-lg px-4 py-3 2xl:px-6 2xl:py-4 w-full">
          <div class="flex items-center font-medium text-sm 2xl:text-base text-gray-800">
            <span class="mr-2 text-purple-600 font-bold"><svg class="w-6 h-6 text-gray-800" aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="m9 5 7 7-7 7" />
              </svg>
            </span> Do I still own the IP?
          </div>
        </div>
        <div class="bg-neutral-300 rounded-lg px-4 py-3 2xl:px-6 2xl:py-4 w-full">
          <div class="flex items-center font-medium text-sm 2xl:text-base text-gray-800">
            <span class="mr-2 text-purple-600 font-bold"><svg class="w-6 h-6 text-gray-800" aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="m9 5 7 7-7 7" />
              </svg>
            </span> Who buys this data?
          </div>
        </div>
        <div class="bg-neutral-300 rounded-lg px-4 py-3 2xl:px-6 2xl:py-4 w-full">
          <div class="flex items-center font-medium text-sm 2xl:text-base text-gray-800">
            <span class="mr-2 text-purple-600 font-bold"><svg class="w-6 h-6 text-gray-800" aria-hidden="true"
                xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="m9 5 7 7-7 7" />
              </svg>
            </span> What is a DAO?
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.hero-container {
  background: linear-gradient(to bottom, #1e293b, #0f172a, #020617);
  height: 4.5rem;
  @apply border-b border-gray-200 dark:border-white/10
}

.logo-container {
  @apply w-[8.5rem] h-[8.5rem] 2xl:w-[10rem] 2xl:h-[10rem] rounded-full bg-white flex items-center justify-center;
}

.logo-text-container {
  @apply flex flex-col;
}

.logo-text-title {
  @apply text-2xl 2xl:text-3xl font-bold bg-gradient-to-r from-fuchsia-500 to-purple-600 bg-clip-text text-transparent leading-tight;
  letter-spacing: 0.05em;
}

.logo-text-subtitle {
  @apply text-xl 2xl:text-2xl font-semibold bg-gradient-to-r from-fuchsia-500 to-purple-600 bg-clip-text text-transparent leading-tight self-end -mt-2.5 mr-[0.9rem] 2xl:mr-[1.4rem];
  letter-spacing: 0.08em;
}
</style>